import {
  createUserPrefs,
  fetchUsersPrefs,
  resolveAndFetchUsers,
  updateUser,
  updateUserPrefs,
  type UpdateUserDTO,
  type UserSettingsDTO,
  type UserSettingsUpdateDTO,
} from "@/api/vtnz-backend/user";
import User from "@/domains/user/user";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type {
  DriverProfile,
  UIPreferences,
  UpdateUserDetailsResult,
  UserPreferences,
  UserPreferenceUpdate,
} from "./types";

export const useUserStore = defineStore("userStore", () => {
  // ---------------------------------- State --------------------------------- //

  const usersFetchState = ref<FetchState>("idle");
  const usersErrorMsg = ref<string | null>(null);
  const users = ref<User[] | null>(null);

  const preferencesFetchState = ref<FetchState>("idle");
  const preferencesErrorMsg = ref<string | null>(null);
  const userPreferences = ref<UserPreferences[] | null>(null);

  // --------------------------------- Getters -------------------------------- //

  const usersIsFetching = computed(() => usersFetchState.value === "loading");
  const preferencesIsFetching = computed(
    () => preferencesFetchState.value === "loading",
  );
  const isFetching = computed(
    () => usersIsFetching.value || preferencesIsFetching.value,
  );

  const getUsers = computed(() => users.value ?? []);

  const getCurrentUser = computed(() =>
    users.value?.find((u) => u.currentUserFlag),
  );

  const getCurrentUserPreferences = computed(() => {
    const user = getCurrentUser.value;

    if (!user) return null;

    return userPreferences.value?.find((p) => p.userId === user.id) ?? null;
  });

  const defaultVehicleExternalId = computed(
    () => getCurrentUserPreferences.value?.selectedVehicleExternalId ?? null,
  );

  // --------------------------------- Actions -------------------------------- //

  async function fetchUsers() {
    usersFetchState.value = "loading";
    usersErrorMsg.value = null;

    const fetchRes = await resolveAndFetchUsers();

    if (!fetchRes.success) {
      usersErrorMsg.value = fetchRes.errorMsg;
      usersFetchState.value = "error";
      return;
    }

    users.value = fetchRes.data.map((u) => User.fromDTO(u));
    usersFetchState.value = "success";
  }

  async function fetchUserPrefs() {
    preferencesFetchState.value = "loading";
    preferencesErrorMsg.value = null;

    const fetchRes = await fetchUsersPrefs();

    if (!fetchRes.success) {
      preferencesErrorMsg.value = fetchRes.errorMsg;
      preferencesFetchState.value = "error";
      return;
    }

    userPreferences.value = fetchRes.data.map((dto) =>
      userPreferencesFromDTO(dto),
    );

    preferencesFetchState.value = "success";
  }

  async function setAndUpdateUserDetails(
    dataToUpdate: UpdateUserDTO,
  ): Promise<UpdateUserDetailsResult> {
    // `dataToUpdate` always has every key present (callers build it with
    // `key: condition ? value : undefined`), so `Object.keys` alone can't
    // detect "nothing changed" - only a value-level check can.
    const hasFieldsToUpdate = Object.values(dataToUpdate).some(
      (value) => value !== undefined,
    );
    if (!hasFieldsToUpdate) {
      return {
        success: false,
        errorMsg: "There are no user details to update.",
      };
    }

    const userId = getCurrentUser.value?.id;
    if (!userId) {
      return {
        success: false,
        errorMsg:
          "Unable to update user details because the current user could not be identified.",
      };
    }

    const response = await updateUser(userId, dataToUpdate);
    if (!response.success) {
      return {
        success: false,
        errorMsg: response.errorMsg,
      };
    }

    // Keep the local user state in sync with the successful update.
    getCurrentUser.value?.updateData(dataToUpdate);

    return {
      success: true,
    };
  }

  async function setAndUpdatePrefs(
    prefsToChange: UserPreferenceUpdate | UserPreferenceUpdate[],
  ): Promise<void> {
    const currentUser = getCurrentUser.value;

    if (!currentUser) return;

    const currentUserPrefs = getCurrentUserPreferences.value;

    // Copied rather than mutated in place - `currentUserPrefs` is the same
    // object already sitting in `userPreferences.value`, and it must not be
    // touched until the write below actually succeeds.
    const newPrefs: UserPreferences = currentUserPrefs
      ? {
          userId: currentUserPrefs.userId,
          driverProfile: { ...currentUserPrefs.driverProfile },
          selectedVehicleExternalId: currentUserPrefs.selectedVehicleExternalId,
          uiPreferences: { ...currentUserPrefs.uiPreferences },
        }
      : {
          userId: currentUser.id,
          driverProfile: {},
          selectedVehicleExternalId: null,
          uiPreferences: {},
        };

    const changes = Array.isArray(prefsToChange)
      ? prefsToChange
      : [prefsToChange];

    const driverKeys = new Set(["accelerationAdjustment", "speedAdjustment"]);

    for (const change of changes) {
      if (change.key === "selectedVehicleExternalId") {
        newPrefs.selectedVehicleExternalId = change.value as string | null;
      } else if (driverKeys.has(change.key)) {
        newPrefs.driverProfile[change.key as keyof DriverProfile] =
          change.value as DriverProfile[keyof DriverProfile];
      } else {
        newPrefs.uiPreferences[change.key] = change.value;
      }
    }

    const payload = userPreferencesToDTO(newPrefs);

    const persistRes = currentUserPrefs
      ? await updateUserPrefs(currentUser.id, payload)
      : await createUserPrefs({ user_id: currentUser.id, ...payload });

    if (!persistRes.success) return;

    const currentUserPrefsIndex = userPreferences.value?.findIndex(
      (p) => p.userId === newPrefs.userId,
    );

    if (currentUserPrefsIndex !== undefined && currentUserPrefsIndex >= 0) {
      userPreferences.value!.splice(currentUserPrefsIndex, 1, newPrefs);
    } else {
      userPreferences.value?.push(newPrefs);
    }
  }

  function $reset() {
    users.value = null;
    usersFetchState.value = "idle";
    usersErrorMsg.value = null;
    userPreferences.value = null;
    preferencesFetchState.value = "idle";
    preferencesErrorMsg.value = null;
  }

  return {
    // ---------------------------------- State --------------------------------- //

    /** indicates the stores current user data fetching state */
    usersFetchState,
    /** error message for last user fetching error if there is one. */
    usersErrorMsg,
    /** indicates the stores current user preferences data fetching state */
    preferencesFetchState,
    /** error message for last user preferences fetching error if there is one. */
    preferencesErrorMsg,

    // --------------------------------- Getters -------------------------------- //

    /** helper flag to indicated async user fetching is in operation. Good for loading animations. */
    usersIsFetching,
    /** helper flag to indicated async user preferences fetching is in operation. Good for loading animations. */
    preferencesIsFetching,
    /** helper flag to indicated async fetching is in operation. Good for loading animations. */
    isFetching,
    /** Returns the current users data. */
    getCurrentUser,
    /** Returns the current users preferences. */
    getCurrentUserPreferences,
    /** Returns the current users selected default vehicles external id if there is any. */
    defaultVehicleExternalId,
    /** Returns all fleet users data. */
    getUsers,

    // --------------------------------- Actions -------------------------------- //

    /** fetch users from the backend. */
    fetchUsers,
    /** updates the current users details locally and in the backend. */
    setAndUpdateUserDetails,

    /** fetch user preferences from the backend. */
    fetchUserPrefs,

    /** updates the current users preferences locally and in the backend */
    setAndUpdatePrefs,
    /** Resets the store to its initial state. */
    $reset,
  };
});

function userPreferencesFromDTO(dto: UserSettingsDTO): UserPreferences {
  return {
    userId: dto.user_id,
    driverProfile: dto.driver_profile as DriverProfile,
    selectedVehicleExternalId: dto.selected_vehicle ?? null,
    uiPreferences: dto.ui_prefs_optimiser as UIPreferences,
  };
}

function userPreferencesToDTO(prefs: UserPreferences): UserSettingsUpdateDTO {
  return {
    driver_profile: prefs.driverProfile,
    ui_prefs_optimiser: prefs.uiPreferences,
    ...(prefs.selectedVehicleExternalId !== null
      ? { selected_vehicle: prefs.selectedVehicleExternalId }
      : {}),
  };
}
